import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { courtTypesApi, extractList } from '../../api/facades';

const CourtTypesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<'Yes' | 'No' | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['court-types', search, isActive],
    queryFn: () => courtTypesApi.list({ search: search || undefined, isActive: isActive || undefined }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => courtTypesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-types'] });
      setDeleteId(null);
      toast.success('Court deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'court_type_name', label: 'Name' },
    { key: 'is_active', label: 'Active', render: (r: any) => (r.is_active === 'Yes' ? 'Yes' : 'No') },
  ];

  return (
    <div>
      <PageHeader title="Courts" subtitle="Manage court categories." primaryAction={{ label: 'New Court', to: '/dashboard/settings/court-types/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
        <select value={isActive} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No' | '')} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm">
          <option value="">All</option>
          <option value="Yes">Active</option>
          <option value="No">Inactive</option>
        </select>
        <input type="search" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-48" />
      </div>
        <DataTable columns={columns} data={rows} loading={isLoading} emptyMessage="No courts." onRowClick={(id) => navigate(`/dashboard/settings/court-types/${id}`)} onEdit={(id) => navigate(`/dashboard/settings/court-types/${id}/edit`)} onDelete={(id) => setDeleteId(Number(id))} />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete court" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default CourtTypesListPage;
